import React, { memo, useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import isEqual from "react-fast-compare";
import { cn } from "../../../utils";
import { prettierTitle } from "../utils";
import { useSchema } from "../../../hooks/useStore";
import { getWidgetSetup } from "../renderingHash";
import { ViewContext } from "../View";

export const ValueRenderer = memo((props) => {
  const { infoObjectName, path, name, data, options: _options } = props;
  const options = useMemo(
    () => ({
      ...{
        hideKeys: false,
        isPrimary: false,
        isPreviewValue: false,
      },
      ..._options,
    }),
    [_options]
  );

  const { rendered } = useContext(ViewContext);
  const pointedSchema = useSchema(infoObjectName, path);

  const [content, setContent] = useState(
    typeof data === "string" || typeof data === "number"
      ? data
      : JSON.stringify(data)
  );

  useEffect(() => {
    const newContent =
      typeof data === "string" || typeof data === "number"
        ? data
        : JSON.stringify(data);
    setContent(newContent);
  }, [data]);

  const [widget, setWidget] = useState(getWidgetSetup(pointedSchema, options));

  useEffect(() => {
    setWidget(getWidgetSetup(pointedSchema, options));
  }, [options, pointedSchema]);

  /* rendered setups */
  const hideKeys = rendered
    ? options.hideKeys || widget.options.hideKeys
    : options.hideKeys;
  const renderAsPrimary = rendered
    ? options.isPrimary || widget.isPrimary
    : options.isPrimary;

  return (
    <motion.div
      key={`${infoObjectName}-${path}`}
      className={cn(
        "r-value-container",
        hideKeys && "r-minimal",
        rendered && "r-rendered-container",
        renderAsPrimary && "r-primary"
      )}
      initial={false}
      animate={{ opacity: 1 }}
      data-render={pointedSchema.render ?? ""}
      data-function={pointedSchema.function ?? ""}
      data-widget={widget.name ?? ""}
    >
      {!!name && !hideKeys && (
        <motion.span
          key={`${infoObjectName}-${path}-key`}
          className={cn("r-key", rendered && "r-rendered")}
        >
          {prettierTitle(name)}
        </motion.span>
      )}

      <AnimatePresence>
        {rendered ? (
          widget.render(`${infoObjectName}-${path}-content`, props)
        ) : (
          <motion.span
            key={`${infoObjectName}-${path}-content`}
            className="r-value"
            layoutId={`${infoObjectName}-${path}-content`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            // transition={{ duration: 0.2 }}
          >
            {content}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}, isEqual);
